<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: EventCalendar                                         |
| Section: views/overviews                                         |
| Purpose: Calendar route presenting date-based family events.     |
+------------------------------------------------------------------+
-->
<template>
    <div class="ResearchPage_wrapper wrapper">
        <div>
            <!-- Title Section -->
            <h1>{{ $t('eventCalendar.title') }}</h1>
            <p class="subtitle text-center">{{ $t('eventCalendar.subtitle') }}</p>

            <!-- Date Picker with Prev/Next Arrows -->
            <div class="date_picker">
                <div class="date_nav">
                    <button class="nav-arrow" @click="prevDay" aria-label="Previous day">&#8249;</button>
                    <div class="dropdowns">
                        <CustomSelect :options="dayOptions" v-model="selectedDay" @update:modelValue="fetchEvents"
                            wrapperClass="custom-select--96px" />
                        <CustomSelect :options="monthOptions" v-model="selectedMonth" @update:modelValue="fetchEvents"
                            wrapperClass="custom-select--144px" />
                    </div>
                    <button class="nav-arrow" @click="nextDay" aria-label="Next day">&#8250;</button>
                </div>
                <button class="today-btn" @click="goToToday">{{ $t('eventCalendar.today') }}</button>
            </div>

            <!-- Summary Bar -->
            <div class="summary-bar" v-if="eventSummaryItems.length">
                <span v-for="(item, i) in eventSummaryItems" :key="item.type">
                    <span v-if="i > 0" class="summary-sep"> · </span>
                    <span class="summary-item">
                        <span class="summary-dot" :class="`event-dot--${item.type}`"></span>{{ item.count }} {{
                            item.label }}
                    </span>
                </span>
            </div>

            <!-- Event Type Filter Toggles -->
            <div class="event-type-filters">
                <button class="filter-btn" :class="{ active: activeEventTypeFilter === null }"
                    @click="setEventTypeFilter(null)">
                    {{ $t('eventCalendar.filterAll') }}
                </button>
                <button v-for="type in eventOrder" :key="type" class="filter-btn"
                    :class="[`filter-btn--${type}`, { active: activeEventTypeFilter === type }]"
                    @click="setEventTypeFilter(type)">
                    {{ localizedEventTypeLabels[type] }}
                </button>
            </div>

            <CalendarWidget :selectedDate="formattedDate" :overrideEventCounts="filteredEventCountsForHeatmap"
                @dateSelected="handleDateSelected" />
        </div>

        <!-- Loading Spinner -->
        <LoadingSpinner v-if="loading" />

        <!-- Timeline -->
        <div v-else>
            <section class="timeline" v-if="timelineByCentury.length">
                <div class="century-group" v-for="century in timelineByCentury" :key="century.centuryStart">
                    <div class="century-rail">
                        <button class="century-header" @click="toggleCentury(century.centuryStart)">
                            <span>{{ century.centuryLabel }}</span>
                            <span class="century-chevron">{{ expandedCenturies[century.centuryStart] === false ? '›' :
                                '▾'
                                }}</span>
                        </button>
                        <div class="century-line" v-show="expandedCenturies[century.centuryStart] !== false"></div>
                    </div>
                    <div class="timeline-entries" v-show="expandedCenturies[century.centuryStart] !== false">
                        <div class="timeline-entry" v-for="(person, idx) in century.entries"
                            :key="`${person.nodeID || person.personID || 'event'}-${person.eventType}-${idx}`">
                            <div class="entry-meta">
                                <span class="event-dot" :class="`event-dot--${person.eventType}`"></span>
                                <span v-if="!isCalendarEventPlaceholder(person)" class="entry-year">{{ person.eventDate ? new Date(person.eventDate).getFullYear()
                                    : '?' }}</span>
                            </div>
                            <PersonCard :photoSrc="person.photoSrc"
                                :name="calendarPersonName(person)"
                                :years="calendarEventDescription(person)" :gender="calendarPersonGender(person)"
                                :location="calendarPersonLocation(person)" :dateIcon="true"
                                :href="calendarPersonHref(person)"
                                :personID="person.personID" :firstname="person.firstname" :lastname="person.lastname"
                                :living="person.living" :placeholder="isCalendarEventPlaceholder(person)"
                                :isSmall="true" :class="{ 'PersonCard_small_guest': isPrivatePerson(person) }" />
                        </div>
                    </div>
                </div>
            </section>
            <p v-else class="no-events-msg">—</p>
        </div>

    </div>
    <ContactCta />
</template>

<script>
import PersonCard from '@/atoms/PersonCard.vue';
import CustomSelect from '@/atoms/CustomSelect.vue';
import LoadingSpinner from '@/atoms/LoadingSpinner.vue';
import { getDefaultPhoto } from '@/utils/photoUtils';
import { useAuth } from '@/composables/useAuth';
import { useLocale } from '@/composables/useLocale';
// import axios from 'axios';
import CalendarWidget from '@/components/CalendarWidget.vue';
import ContactCta from '@/components/ContactCta.vue';
import { applyGenericSeo, applyBreadcrumbSeo } from '@/utils/seoUtils';
import { fetchRuntimeJson } from '@/utils/runtimeJson';
import { isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';
import { fetchTodayEvents } from '@/api/apiService';

export default {
    components: {
        PersonCard,
        CalendarWidget,
        CustomSelect,
        LoadingSpinner,
        ContactCta,
    },
    setup() {
        const { authToken } = useAuth();
        const { localePath } = useLocale();
        return { authToken, localePath };
    },
    data() {
        // Initialize date from URL query parameter if available
        let initialMonth = new Date().getMonth() + 1;
        let initialDay = new Date().getDate();

        const dateParam = this.$route.query.date;
        if (dateParam) {
            const match = dateParam.match(/^(\d{2})-(\d{2})$/);
            if (match) {
                const [, day, month] = match;
                initialMonth = parseInt(month, 10);
                initialDay = parseInt(day, 10);
            }
        }

        return {
            events: {}, // Store events fetched from the API
            selectedMonth: initialMonth,
            selectedDay: initialDay,
            loading: false,
            eventOrder: ["birth", "baptism", "death", "burial"],
            activeEventTypeFilter: null,
            eventCountsByType: {},
            expandedCenturies: {}
        };
    },
    computed: {
        dayOptions() {
            return this.daysInMonth.map((day) => ({ label: day, value: day }));
        },
        monthOptions() {
            return this.localizedMonths.map((month, index) => ({
                label: month,
                value: index + 1,
            }));
        },
        localizedMonths() {
            const months = this.$tm('eventCalendar.months');
            return months.map(m => typeof m === 'function' ? m() : String(m));
        },
        localizedEventTypeLabels() {
            return {
                birth: this.$t('eventCalendar.eventTypes.birth'),
                death: this.$t('eventCalendar.eventTypes.death'),
                baptism: this.$t('eventCalendar.eventTypes.baptism'),
                burial: this.$t('eventCalendar.eventTypes.burial'),
            };
        },
        daysInMonth() {
            // Dynamically calculate the number of days in the selected month
            return new Array(new Date(2024, this.selectedMonth, 0).getDate()).fill(null).map((_, i) => i + 1);
        },
        formattedDate() {
            // Format as MM-DD
            return `${String(this.selectedMonth).padStart(2, "0")}-${String(this.selectedDay).padStart(2, "0")}`;
        },
        filteredEventData() {
            if (!this.activeEventTypeFilter) return this.events;
            const filtered = {};
            if (this.events[this.activeEventTypeFilter]) {
                filtered[this.activeEventTypeFilter] = this.events[this.activeEventTypeFilter];
            }
            return filtered;
        },
        orderedEventData() {
            const orderedData = {};
            this.eventOrder.forEach(eventType => {
                if (this.filteredEventData[eventType]) {
                    orderedData[eventType] = this.filteredEventData[eventType];
                }
            });
            return orderedData;
        },
        eventSummaryItems() {
            return this.eventOrder
                .filter(type => this.events[type] && this.events[type].length > 0)
                .map(type => ({
                    type,
                    label: this.localizedEventTypeLabels[type],
                    count: this.events[type].length
                }));
        },
        filteredEventCountsForHeatmap() {
            if (!this.activeEventTypeFilter) return null;
            const result = {};
            Object.entries(this.eventCountsByType).forEach(([date, typeCounts]) => {
                result[date] = typeCounts[this.activeEventTypeFilter] || 0;
            });
            return result;
        },
        timelineByCentury() {
            const flat = [];
            Object.entries(this.filteredEventData).forEach(([eventType, members]) => {
                members.forEach(person => flat.push({ ...person, eventType }));
            });
            flat.sort((a, b) => {
                const yearA = a.eventDate ? new Date(a.eventDate).getFullYear() : 0;
                const yearB = b.eventDate ? new Date(b.eventDate).getFullYear() : 0;
                return yearA - yearB;
            });
            const centuryMap = {};
            flat.forEach(entry => {
                const year = entry.eventDate ? new Date(entry.eventDate).getFullYear() : 0;
                const cs = Math.floor(year / 100) * 100;
                if (!centuryMap[cs]) centuryMap[cs] = [];
                centuryMap[cs].push(entry);
            });
            return Object.keys(centuryMap)
                .sort((a, b) => Number(a) - Number(b))
                .map(cs => ({
                    centuryStart: Number(cs),
                    centuryLabel: `${cs}s`,
                    entries: centuryMap[cs]
                }));
        }
    },
    async created() {
        document.title = "Kalender | Sejarah Thümann";
        await Promise.all([
            this.loadEventCountsByType(),
            this.fetchEvents()
        ]);
    },
    mounted() {
        const title = "Kalender | Sejarah Thümann";
        const description = "Ontdek wat er gebeurde op deze dag in de tijd.";
        const image = "https://sejarah-thumann.online/img/sejarah_thumann1080x781.4fc54a6a.jpg";
        const url = window.location.href;

        applyGenericSeo({ title, description, image, url }, { includeTwitter: true });

        // Add breadcrumb schema
        applyBreadcrumbSeo([
            { name: 'Home', url: 'https://sejarah-thumann.online/' },
            { name: 'Kalender', url: 'https://sejarah-thumann.online/kalender' }
        ]);
    },
    watch: {
        selectedMonth() {
            this.updateUrlQueryParam();
        },
        selectedDay() {
            this.updateUrlQueryParam();
        }
    },
    methods: {
        getDefaultPhoto,
        isPrivatePerson(person) {
            return isPrivateLivingPerson(person, this.authToken);
        },
        prevDay() {
            const d = new Date(2024, this.selectedMonth - 1, this.selectedDay);
            d.setDate(d.getDate() - 1);
            this.selectedMonth = d.getMonth() + 1;
            this.selectedDay = d.getDate();
            this.fetchEvents();
        },
        nextDay() {
            const d = new Date(2024, this.selectedMonth - 1, this.selectedDay);
            d.setDate(d.getDate() + 1);
            this.selectedMonth = d.getMonth() + 1;
            this.selectedDay = d.getDate();
            this.fetchEvents();
        },
        goToToday() {
            const today = new Date();
            this.selectedMonth = today.getMonth() + 1;
            this.selectedDay = today.getDate();
            this.fetchEvents();
        },
        setEventTypeFilter(type) {
            this.activeEventTypeFilter = type;
        },
        toggleCentury(centuryStart) {
            this.expandedCenturies[centuryStart] = this.expandedCenturies[centuryStart] !== false ? false : true;
        },
        updateUrlQueryParam() {
            // Create a date string in DD-MM format for the URL
            const dateString = `${String(this.selectedDay).padStart(2, '0')}-${String(this.selectedMonth).padStart(2, '0')}`;

            // Update URL without adding to history
            if (this.$route.query.date !== dateString) {
                this.$router.replace({
                    query: { ...this.$route.query, date: dateString }
                });
            }
        },
        handleDateSelected(dateStr) {
            // dateStr is in "MM-DD" format.
            const [month, day] = dateStr.split("-");
            this.selectedMonth = parseInt(month);
            this.selectedDay = parseInt(day);
            // Optionally re-fetch events for the newly selected date:
            this.fetchEvents();
        },
        async fetchEvents() {
            this.loading = true;
            try {
                const eventsArray = await fetchTodayEvents(this.formattedDate);

                const groupedEvents = {};
                (Array.isArray(eventsArray) ? eventsArray : []).forEach(event => {
                    if (!event.placeholder && (!event.photoSrc || event.photoSrc.trim() === '')) {
                        event.photoSrc = this.getDefaultPhoto(event.sex);
                    }

                    if (!groupedEvents[event.eventType]) {
                        groupedEvents[event.eventType] = [];
                    }
                    groupedEvents[event.eventType].push(event);
                });

                this.events = groupedEvents;
            } catch (error) {
                console.error('Error loading events for', this.formattedDate, error);
                this.events = {};
            } finally {
                this.loading = false;
            }
        },
        async loadEventCountsByType() {
            const counts = await fetchRuntimeJson('/data/getEventCounts/eventCountsByType.json', {});
            this.eventCountsByType = counts && typeof counts === 'object' && !Array.isArray(counts)
                ? counts
                : {};
        },
        // async fetchEvents() {
        //     this.loading = true;
        //     try {
        //         const response = await fetchTodayEvents(this.formattedDate);
        //         if (!Array.isArray(response)) {
        //             console.error('Expected an array but received:', response);
        //             this.events = {};
        //             this.filteredEventData = {};
        //             return;
        //         }

        //         const groupedEvents = {};
        //         await Promise.all(
        //             response.map(async (event) => {
        //                 // Fetch person data and photo path
        //                 let photoPath = '';
        //                 try {
        //                     const photoResponse = await axios.get(
        //                         `${process.env.VUE_APP_BACKEND_API_URL}/person-default-photo/${event.personID}`,
        //                         {
        //                             headers: {
        //                                 'X-API-KEY': process.env.VUE_APP_API_KEY
        //                             }
        //                         }
        //                     );
        //                     photoPath = photoResponse.data
        //                         ? `${process.env.VUE_APP_PHOTO_API_URL}/${encodeURIComponent(photoResponse.data.path)}`
        //                         : '';
        //                 } catch (error) {
        //                     console.error(`Error fetching profile image for personID ${event.personID}:`, error);
        //                 }

        //                 // Determine gender for fallback photo
        //                 const gender = event.sex === 'M' ? 'M' : event.sex === 'F' ? 'F' : null;

        //                 // Augment event data with photoSrc and fallback logic
        //                 const augmentedEvent = {
        //                     ...event,
        //                     photoSrc: photoPath || getDefaultPhoto(gender),
        //                 };

        //                 // Group events by eventType
        //                 if (!groupedEvents[event.eventType]) {
        //                     groupedEvents[event.eventType] = [];
        //                 }
        //                 groupedEvents[event.eventType].push(augmentedEvent);
        //             })
        //         );

        //         this.events = groupedEvents;
        //         this.filteredEventData = groupedEvents;
        //     } catch (error) {
        //         console.error('Error fetching events:', error);
        //     } finally {
        //         this.loading = false;  // Stop spinner
        //     }
        // },
        getProfileImageSrc(person) {
            return person.photoSrc || (person.sex === 'M' ? this.defaultPhotoMale : this.defaultPhotoFemale);
        },
        formatEventDescription(eventType, eventDate) {
            if (!eventDate) return '';
            const year = new Date(eventDate).getFullYear();
            const key = `eventCalendar.eventDescriptions.${eventType}`;
            const fallback = this.$t('eventCalendar.eventDescriptions.unknown', { year });
            const translated = this.$t(key, { year });
            return translated === key ? fallback : translated;
        },
        isCalendarEventPlaceholder(person) {
            return person?.placeholder === true
                || isLivingPlaceholder(person)
                || isPrivateLivingPerson(person, this.authToken);
        },
        calendarPersonName(person) {
            if (this.isCalendarEventPlaceholder(person)) return this.$t('privacy.livingPlaceholder');
            return [person?.firstname, person?.lastname].filter(Boolean).join(' ');
        },
        calendarPersonGender(person) {
            return this.isCalendarEventPlaceholder(person) ? 'unknown' : (person?.sex || 'unknown');
        },
        calendarPersonHref(person) {
            return this.isCalendarEventPlaceholder(person) || !person?.personID
                ? 'javascript:void(0)'
                : this.localePath(`/person/${person.personID}`);
        },
        calendarEventDescription(person) {
            return this.isCalendarEventPlaceholder(person)
                ? ''
                : this.formatEventDescription(person?.eventType, person?.eventDate);
        },
        calendarPersonLocation(person) {
            return this.isCalendarEventPlaceholder(person) ? '' : (person?.eventPlace || '');
        }
    }
};
</script>

<style scoped>
.date_picker {
    /* display: flex; */
    display: none;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

.date_picker label {
    font-size: 18px;
    margin-bottom: 8px;
}

.date_nav {
    display: flex;
    align-items: center;
    gap: 8px;
}

.date_picker .dropdowns {
    display: flex;
    gap: 8px;
}

.nav-arrow {
    background: none;
    border: 1px solid var(--brand-bg-6);
    border-radius: var(--border-radius-sm);
    width: 36px;
    height: 36px;
    font-size: 22px;
    line-height: 1;
    cursor: pointer;
    color: var(--brand-text-dark);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.15s;
}

.nav-arrow:hover {
    background-color: var(--brand-bg-2);
}

.today-btn {
    background: none;
    border: 1px solid var(--brand-bg-6);
    border-radius: var(--border-radius-sm);
    padding: 4px 14px;
    font-size: 13px;
    cursor: pointer;
    color: var(--brand-text-dark);
    transition: background-color 0.15s;
}

.today-btn:hover {
    background-color: var(--brand-bg-2);
}

select {
    padding: 8px 12px;
    font-size: 16px;
    border: 1px solid #ccc;
    border-radius: 4px;
}

.subtitle {
    margin-top: 16px;
    margin-bottom: calc(16px + 16px);
}

/* Summary bar */
.summary-bar {
    /* display: flex; */
    display: none;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-top: 14px;
    font-size: 13px;
    color: var(--brand-text-dark);
    opacity: 0.8;
}

.summary-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.summary-sep {
    color: var(--brand-bg-6);
}

/* Event type filter buttons */
.event-type-filters {
    display: flex;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 16px;
}

.filter-btn {
    background: none;
    border: 1px solid var(--brand-bg-6);
    border-radius: 20px;
    padding: 4px 14px;
    font-size: 13px;
    cursor: pointer;
    color: var(--brand-text-dark);
    transition: background-color 0.15s, border-color 0.15s, color 0.15s;
}

.filter-btn:hover {
    background-color: var(--brand-bg-2);
}

.filter-btn.active {
    background-color: var(--brand-text-dark);
    color: var(--brand-text-light);
    border-color: var(--brand-text-dark);
}

.filter-btn--birth.active {
    background-color: #3d8c40;
    border-color: #3d8c40;
    color: #fff;
}

.filter-btn--baptism.active {
    background-color: #1565c0;
    border-color: #1565c0;
    color: #fff;
}

.filter-btn--death.active {
    background-color: var(--brand-color-1);
    border-color: var(--brand-color-1);
    color: #fff;
}

.filter-btn--burial.active {
    background-color: var(--brand-bg-3);
    border-color: var(--brand-bg-3);
    color: #fff;
}

/* Event-type dots */
.event-dot,
.summary-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
}

.event-dot--birth,
.summary-dot--birth {
    background-color: #3d8c40;
}

.event-dot--baptism,
.summary-dot--baptism {
    background-color: #1565c0;
}

.event-dot--death,
.summary-dot--death {
    background-color: var(--brand-color-1);
}

.event-dot--burial,
.summary-dot--burial {
    background-color: var(--brand-bg-3);
}

/* Timeline */
.timeline {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.century-group {
    /* margin-bottom: 4px; */
    display: flex;
    align-items: stretch;
    gap: 24px;
}

.century-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex-shrink: 0;
}

.century-header {
    background: none;
    border: none;
    /* border-bottom: 1px solid var(--brand-bg-7); */
    font-family: var(--font-secondary);
    font-size: 36px;
    font-weight: 600;
    cursor: pointer;
    padding: 20px 0 16px 0;
    display: flex;
    align-items: center;
    gap: 6px;
    color: var(--brand-color-1);
    /* width: 100%; */
    text-align: left;
    margin-top: 32px;
}

.century-line {
    width: 2px;
    flex: 1;
    background-color: var(--brand-bg-4);
    opacity: .2;
    margin-right: 16px;
    border-radius: 999px;
}

.century-chevron {
    font-size: 14px;
    opacity: 0.5;
}

.timeline-entries {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
}

.timeline-entry {
    display: flex;
    align-items: center;
    /* gap: 16px; */
    /* padding: 16px 0; */
    /* border-bottom: 1px solid var(--brand-bg-7); */
}

.entry-meta {
    /* display: flex; */
    display: none;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    min-width: 36px;
    padding-top: 14px;
}

.entry-year {
    font-size: 11px;
    color: var(--brand-text-dark);
    white-space: nowrap;
}

.no-events-msg {
    text-align: center;
    color: var(--brand-bg-6);
    margin-top: 32px;
}

.timeline :deep(.PersonCard--small .PersonCard_photo) {
    width: 80px;
    height: 80px;
}

@media (max-width: 720px) {
    .century-rail {
        display: none;
    }

    .timeline :deep(.PersonCard--small .PersonCard_photo) {
        width: 48px;
        height: 48px;
    }
}
</style>
